import { Alert, AlertDescription, Badge, Card, Spinner } from '@/components/ui'
import { useCapabilities } from '@/hooks'
import type { Capability } from '@/types/toolFramework'

function stabilityVariant(stability: string): 'default' | 'secondary' | 'outline' {
  if (stability === 'stable') return 'default'
  if (stability === 'beta') return 'secondary'
  return 'outline'
}

function CapabilityCard({ cap }: { cap: Capability }) {
  return (
    <Card className="p-4 gap-0 hover:shadow-xs transition-shadow">
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="font-mono text-sm font-medium text-foreground break-all">
          {cap.id}
        </span>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <Badge variant="secondary">{cap.category}</Badge>
          <Badge variant={stabilityVariant(cap.stability)}>{cap.stability}</Badge>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-3">
        {cap.description ?? <em className="text-muted-foreground">No description</em>}
      </p>
      {cap.recommended_default_adapter && (
        <p className="text-xs text-muted-foreground mt-2">
          Default: {cap.recommended_default_adapter}
        </p>
      )}
    </Card>
  )
}

export function CapabilityList() {
  const { data, isLoading, error } = useCapabilities()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {error instanceof Error ? error.message : 'Failed to load capabilities'}
        </AlertDescription>
      </Alert>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-12 text-center">
        No capabilities registered yet.
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {data.map((cap) => (
        <CapabilityCard key={cap.id} cap={cap} />
      ))}
    </div>
  )
}
